import { JOB_STATUS } from '@/lib/db-constants';
import { Job, Map, UserProfile } from '@/lib/db-row-types';
import { getBrowserClient } from '@/utils/supabase/client';
import {
  DILIGENCE_EXPORT_JOB_TYPES,
  DILIGENCE_EXPORT_LIFETIME_MS,
  isDiligenceExportJob,
} from './diligence-exports';

export type JobWithDetails = Job & {
  created_by_user: Pick<UserProfile, 'display_name'> | null;
  map: Pick<Map, 'title'> | null;
};

/**
 * Builds a `JobWithDetails` from a raw realtime row when the follow-up
 * `fetchJobWithDetails` read-after-write races and comes back empty (e.g. a
 * fresh INSERT the join hasn't caught up to yet). Missing the joined
 * `created_by_user`/`map` display fields for one render is far better than
 * silently dropping the job — the next update carries the full join.
 */
export function toJobWithDetailsFallback(job: Job): JobWithDetails {
  return { ...job, created_by_user: null, map: null };
}

const JOB_WITH_DETAILS_SELECT = `
  *,
  created_by_user:user_profiles!jobs_created_by_fkey(display_name),
  map:maps!jobs_map_id_fkey(title)
`;

/**
 * Fetches all jobs for a specific workspace that are in processing or failed status.
 * @param workspaceId The workspace ID to fetch jobs for.
 * @returns Jobs data or error.
 */
export async function fetchWorkspaceActiveJobs(workspaceId: string) {
  const supabase = getBrowserClient();
  const { data, error } = await supabase
    .from('jobs')
    .select(JOB_WITH_DETAILS_SELECT)
    .eq('workspace_id', workspaceId)
    // Layer-scoped jobs are surfaced inline on the layer row's spinner — they
    // must not also appear in the global "Background jobs" menu (GEO-700).
    .is('layer_id', null)
    // Export fragment children are internal fan-out units of their parent
    // export job — only the parent belongs in the menu.
    .is('parent_job_id', null)
    .in('status', [JOB_STATUS.UPLOADING, JOB_STATUS.PROCESSING, JOB_STATUS.FAILED])
    .order('created_at', { ascending: false });

  if (error || !data) {
    return { error, data: null };
  }

  // Failed diligence exports are shown (bounded to 48h) via the dedicated
  // export surface instead — exclude them here so they don't linger in the
  // active-jobs list forever.
  const jobsWithDetails: JobWithDetails[] = (data as JobWithDetails[]).filter(
    job => !(job.status === JOB_STATUS.FAILED && isDiligenceExportJob(job))
  );

  return { data: jobsWithDetails, error };
}

/**
 * Fetches completed/failed diligence exports (PDF, PPTX or Excel) within their
 * downloadable lifetime for a workspace — the reload-survival source for the
 * jobs-menu (48h) and site report modal export surfaces, kept fresh afterwards
 * by the same `jobs` Realtime subscription `use-workspace-jobs.ts` already
 * maintains. Bounded by `DILIGENCE_EXPORT_LIFETIME_MS`, so it cannot drop an
 * export the modal still offers to download.
 * @param workspaceId The workspace ID to fetch exports for.
 * @returns Jobs data or error.
 */
export async function fetchRecentDiligenceExports(workspaceId: string) {
  const supabase = getBrowserClient();
  const oldestDownloadable = new Date(Date.now() - DILIGENCE_EXPORT_LIFETIME_MS).toISOString();
  const { data, error } = await supabase
    .from('jobs')
    .select(JOB_WITH_DETAILS_SELECT)
    .eq('workspace_id', workspaceId)
    .in('payload->>job_type', [...DILIGENCE_EXPORT_JOB_TYPES])
    .in('status', [JOB_STATUS.COMPLETED, JOB_STATUS.FAILED])
    .gte('created_at', oldestDownloadable)
    .order('created_at', { ascending: false });

  if (error || !data) {
    return { error, data: null };
  }

  return { data: data as JobWithDetails[], error: null };
}

/**
 * Fetches a single job with its details (user and map).
 * @param jobId The job ID to fetch.
 * @returns Job data with details or error.
 */
export async function fetchJobWithDetails(jobId: string) {
  const supabase = getBrowserClient();
  const { data, error } = await supabase
    .from('jobs')
    .select(
      `
      *,
      created_by_user:user_profiles!jobs_created_by_fkey(display_name),
      map:maps!jobs_map_id_fkey(title)
    `
    )
    .eq('id', jobId)
    .single();

  if (error || !data) {
    return { error, data: null };
  }

  return { data: data as JobWithDetails, error: null };
}
